<script setup lang="ts">
import { computed } from 'vue';
import { IonContent, IonFooter, IonIcon, IonRefresher, IonRefresherContent, RefresherCustomEvent } from '@ionic/vue';
import { alertCircle, bagRemoveOutline, bodyOutline, call, carSportOutline, documentText, eyeOutline, flameOutline, helpCircleOutline, medkitOutline, searchCircleOutline, shieldCheckmarkOutline, warning } from 'ionicons/icons';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import NavigationBar from '@/shared/components/ui/NavigationBar.vue';
import useSafety, { IncidentType } from '@/modules/safety/composables/useSafety';

const { t } = useI18n();
const router = useRouter();

const { getSafetyConfig, getMyReports, getActiveBroadcasts, ackBroadcast } = useSafety();
const { config, refetchConfig } = getSafetyConfig();
const { reports, refetchReports } = getMyReports();
const { broadcasts, refetchBroadcasts } = getActiveBroadcasts();

const openReportsCount = computed(() => reports.value.filter((r) => ['open', 'assigned', 'in_progress'].includes(r.status)).length);

const goToReport = (type: IncidentType) => {
	router.push({ name: 'safety-report', params: { type } });
};

const goToSos = () => {
	router.push({ name: 'safety-sos' });
};

const goToMyReports = () => {
	router.push({ name: 'safety-my-reports' });
};

const dismissBroadcast = async (id: number) => {
	await ackBroadcast(id);
};

const onRefresh = async (ev: RefresherCustomEvent) => {
	await Promise.all([refetchConfig(), refetchReports(), refetchBroadcasts()]);
	ev.target.complete();
};

const iconFor = (type: IncidentType) => {
	switch (type) {
		case 'medical':
			return medkitOutline;
		case 'fire':
			return flameOutline;
		case 'security':
			return shieldCheckmarkOutline;
		case 'aggression':
			return bodyOutline;
		case 'lost_person':
			return searchCircleOutline;
		case 'theft':
			return bagRemoveOutline;
		case 'suspicious':
			return eyeOutline;
		case 'accident':
			return carSportOutline;
		default:
			return helpCircleOutline;
	}
};
</script>

<template>
	<ion-content>
		<ion-refresher class="mt-8" slot="fixed" @ionRefresh="onRefresh">
			<ion-refresher-content />
		</ion-refresher>

		<div class="px-[34px] pt-14 pb-[120px] w-full max-w-[600px] mx-auto">
			<div class="mb-6">
				<div class="font-sourcesanspro-semibold text-xs2 text-white/60 leading-none">
					{{ t('navigation.safety') }}
				</div>
				<h1 class="font-sourcesanspro-semibold text-m2 text-white leading-tight">
					{{ t('safety.title') }}
				</h1>
			</div>

			<div v-if="config?.enabled === false" class="rounded backdrop-blur-xl bg-white/10 text-white p-4">
				<div class="font-sourcesanspro-semibold text-base2">{{ t('safety.disabled') }}</div>
			</div>

			<template v-else>
				<!-- Active broadcasts -->
				<div v-if="broadcasts.length" class="mb-4 space-y-2">
					<div
						v-for="b in broadcasts"
						:key="b.id"
						class="rounded p-4 flex items-start gap-3 font-sourcesanspro-regular"
						:class="{
							'bg-red-600 text-white': b.priority === 'critical',
							'bg-yellow-500 text-black': b.priority === 'warning',
							'bg-blue-600 text-white': b.priority === 'info',
						}">
						<ion-icon :icon="warning" class="text-l2 shrink-0 mt-0.5" />
						<div class="flex-1">
							<div class="font-sourcesanspro-semibold text-base2">{{ b.title }}</div>
							<div class="text-sm2 opacity-90">{{ b.body }}</div>
						</div>
						<button class="text-sm2 underline opacity-80" @click="dismissBroadcast(b.id)">
							{{ t('general.dismiss') }}
						</button>
					</div>
				</div>

				<!-- SOS -->
				<button class="w-full h-[80px] rounded bg-red-600 text-white px-4 flex items-center justify-center gap-3 active:scale-[0.98] transition-transform" @click="goToSos">
					<ion-icon :icon="alertCircle" class="text-l2" />
					<span class="font-sourcesanspro-semibold text-l2 uppercase">{{ t('safety.sos') }}</span>
				</button>
				<p class="text-center text-white/60 text-xs2 font-sourcesanspro-regular mt-2">{{ t('safety.sos_description') }}</p>

				<!-- Incident types grid -->
				<h2 class="text-white font-sourcesanspro-semibold text-l2 mt-8 mb-3">{{ t('safety.report_something') }}</h2>
				<div class="grid grid-cols-2 gap-2">
					<button v-for="type in config?.incident_types ?? []" :key="type" class="h-[92px] rounded backdrop-blur-xl bg-white/10 text-white px-3 flex flex-col items-center justify-center gap-2 active:bg-white/20" @click="goToReport(type)">
						<span class="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
							<ion-icon :icon="iconFor(type)" class="text-[22px]" />
						</span>
						<span class="font-sourcesanspro-semibold text-sm2 text-center leading-tight">{{ t(`safety.types.${type}`) }}</span>
					</button>
				</div>

				<!-- My reports -->
				<button class="mt-8 w-full h-[80px] rounded backdrop-blur-xl bg-white/10 text-white px-4 flex items-center gap-3 active:bg-white/20" @click="goToMyReports">
					<ion-icon :icon="documentText" class="text-l2 shrink-0" />
					<div class="flex-1 text-left">
						<div class="font-sourcesanspro-semibold text-l2 leading-tight">{{ t('safety.my_reports') }}</div>
						<div class="text-white/60 text-xs2 font-sourcesanspro-regular">
							{{ openReportsCount ? t('safety.open_count', { count: openReportsCount }) : t('safety.no_open_reports') }}
						</div>
					</div>
				</button>

				<!-- Info cards -->
				<template v-if="(config?.info_cards?.length ?? 0) > 0">
					<h2 class="text-white font-sourcesanspro-semibold text-l2 mt-8 mb-3">{{ t('safety.good_to_know') }}</h2>
					<div class="space-y-2">
						<div v-for="(card, idx) in config?.info_cards ?? []" :key="idx" class="rounded backdrop-blur-xl bg-white/10 text-white p-4">
							<div class="font-sourcesanspro-semibold text-base2 mb-1">{{ card.title }}</div>
							<div class="font-sourcesanspro-regular text-sm2 text-white/80">{{ card.body }}</div>
						</div>
					</div>
				</template>

				<!-- Emergency contacts -->
				<template v-if="(config?.emergency_contacts?.length ?? 0) > 0">
					<h2 class="text-white font-sourcesanspro-semibold text-l2 mt-8 mb-3">{{ t('safety.emergency_contacts') }}</h2>
					<div class="space-y-2">
						<a v-for="(contact, idx) in config?.emergency_contacts ?? []" :key="idx" :href="`tel:${contact.value}`" class="rounded backdrop-blur-xl bg-white/10 text-white p-4 flex items-center gap-3 active:bg-white/20">
							<ion-icon :icon="call" class="text-l2" />
							<div class="flex-1">
								<div class="font-sourcesanspro-semibold text-base2">{{ contact.label }}</div>
								<div class="text-white/60 text-sm2 font-sourcesanspro-regular">{{ contact.value }}</div>
							</div>
						</a>
					</div>
				</template>
			</template>
		</div>
	</ion-content>

	<ion-footer>
		<NavigationBar item-active="safety" />
	</ion-footer>
</template>
